import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getCvData } from '../data/cvData';
import { Link } from 'react-router-dom';

function CV() {
  const { lang, setLanguage } = useLanguage();
  const data = getCvData(lang);
  const p = data.profile;
  const s = data.sections;

  const handlePrint = () => {
    window.print();
  };

  const toggleLang = () => {
    setLanguage(lang === 'en' ? 'ar' : 'en');
  };

  return (
    <div className="min-h-screen py-8 sm:py-12 bg-slate-950 text-white">
      {/* Top Controls Bar (Hidden during Print) */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 mb-8 print:hidden">
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <Link
              to={lang === 'ar' ? '/ar' : '/'}
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              <svg className="w-4 h-4 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>{lang === 'ar' ? 'العودة للموقع' : 'Back to Portfolio'}</span>
            </Link>
            <span className="text-white/20">|</span>
            <span className="hidden sm:inline-flex items-center gap-2 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              {lang === 'ar' ? 'سيرة ذاتية تنفيذية محدثة • 2026' : 'Executive CV • Verified 2026'}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleLang}
              className="px-4 py-2 rounded-xl text-xs font-bold bg-white/[0.08] hover:bg-white/[0.15] text-white border border-white/10 transition-colors"
              title="Toggle CV Language"
            >
              {lang === 'ar' ? 'English' : 'العربية (Arabic)'}
            </button>
            <button
              onClick={handlePrint}
              className="px-5 py-2 rounded-xl text-xs sm:text-sm font-bold bg-primary-500 hover:bg-primary-400 text-black shadow-lg shadow-primary-500/20 transition-all flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span>{s.printButton}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main CV Sheet (A4-proportioned paper on screen, pure A4 for print) */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <article
          dir={data.dir}
          className="bg-white text-slate-800 rounded-2xl shadow-2xl p-6 sm:p-12 border border-slate-200 print:border-none print:shadow-none print:p-0 print:m-0 print:rounded-none print:text-black font-sans leading-relaxed"
        >
          {/* Header */}
          <header className="flex flex-col md:flex-row justify-between items-start gap-6 pb-6 border-b-2 border-slate-200 mb-6 print:pb-4 print:mb-4">
            <div className="space-y-1.5 flex-1">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-950 tracking-tight">
                {p.name}
              </h1>
              <div className="text-base sm:text-lg font-bold text-amber-700">
                {p.title}
              </div>
              <div className="text-xs sm:text-sm font-semibold text-slate-500">
                {p.scope}
              </div>
              <p className="text-xs text-slate-600 font-medium max-w-xl leading-relaxed pt-1">
                {p.tagline}
              </p>
            </div>

            {/* Direct Contacts Grid */}
            <div className="flex flex-col gap-1.5 text-xs text-slate-600 shrink-0 min-w-[220px]">
              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href={`mailto:${p.email}`} className="font-semibold text-slate-800 hover:text-amber-700 hover:underline">
                  {p.email}
                </a>
              </div>

              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{p.location}</span>
              </div>

              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <a href={p.website} target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-800 hover:text-amber-700 hover:underline">
                  {p.websiteLabel}
                </a>
              </div>

              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
                <a href={p.linkedin} target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-800 hover:text-amber-700 hover:underline">
                  {p.linkedinLabel}
                </a>
              </div>

              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
                </svg>
                <a href={p.github} target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-800 hover:text-amber-700 hover:underline">
                  {p.githubLabel}
                </a>
              </div>
            </div>
          </header>

          {/* Key Executive Credentials Ribbon */}
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 print:gap-2 print:mb-4">
            {data.achievements.map((ach, idx) => (
              <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200 print:bg-slate-50">
                <span className="inline-block text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 mb-1">
                  {ach.badge}
                </span>
                <div className="text-xs font-bold text-slate-900 leading-snug mb-0.5">
                  {ach.title}
                </div>
                <div className="text-[11px] text-slate-600 leading-tight">
                  {ach.detail}
                </div>
              </div>
            ))}
          </section>

          {/* Executive Profile Section */}
          <section className="mb-6 print:mb-4">
            <h2 className="text-xs font-extrabold uppercase tracking-wider text-slate-900 border-b border-slate-200 pb-1.5 mb-3 flex items-center gap-2">
              <span className="w-1 h-3.5 bg-amber-600 rounded"></span>
              <span>{s.summary}</span>
            </h2>
            <div className="p-3.5 bg-slate-50 rounded-lg border-l-4 rtl:border-l-0 rtl:border-r-4 border-amber-600 text-xs sm:text-[13px] text-slate-700 leading-relaxed text-justify">
              {p.summary}
            </div>
          </section>

          {/* Core Technical, Security & Systems Stack */}
          <section className="mb-6 print:mb-4">
            <h2 className="text-xs font-extrabold uppercase tracking-wider text-slate-900 border-b border-slate-200 pb-1.5 mb-3 flex items-center gap-2">
              <span className="w-1 h-3.5 bg-amber-600 rounded"></span>
              <span>{s.skills}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 print:gap-2">
              {data.skillCategories.map((cat, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200 break-inside-avoid">
                  <div className="text-xs font-bold text-slate-900 mb-2">
                    {cat.category}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.skills.map((sk, sIdx) => (
                      <span key={sIdx} className="text-[11px] font-medium bg-white text-slate-800 border border-slate-300 px-2 py-0.5 rounded">
                        {sk}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Professional Experience */}
          <section className="mb-6 print:mb-4">
            <h2 className="text-xs font-extrabold uppercase tracking-wider text-slate-900 border-b border-slate-200 pb-1.5 mb-4 flex items-center gap-2">
              <span className="w-1 h-3.5 bg-amber-600 rounded"></span>
              <span>{s.experience}</span>
            </h2>
            <div className="space-y-4 print:space-y-3">
              {data.experiences.map((exp, idx) => (
                <article key={idx} className="pb-4 border-b border-dashed border-slate-200 last:border-b-0 last:pb-0 break-inside-avoid">
                  <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 mb-1">
                    <h3 className="text-sm font-bold text-slate-950">
                      {exp.title}
                    </h3>
                    <span className="text-xs font-semibold text-amber-800 shrink-0">
                      {exp.period}
                    </span>
                  </div>
                  <div className="text-xs font-semibold text-slate-500 mb-2">
                    {exp.company}{exp.location ? ` • ${exp.location}` : ''}
                  </div>
                  {exp.description && (
                    <p className="text-xs text-slate-700 leading-relaxed text-justify mb-2">
                      {exp.description}
                    </p>
                  )}
                  {exp.skills && exp.skills.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {exp.skills.map((sk, sIdx) => (
                        <span key={sIdx} className="text-[10px] bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded">
                          {sk}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>

          {/* Education */}
          <section className="break-inside-avoid">
            <h2 className="text-xs font-extrabold uppercase tracking-wider text-slate-900 border-b border-slate-200 pb-1.5 mb-4 flex items-center gap-2">
              <span className="w-1 h-3.5 bg-amber-600 rounded"></span>
              <span>{s.education}</span>
            </h2>
            <div className="space-y-4 print:space-y-3">
              {data.education.map((edu, idx) => (
                <article key={idx} className="pb-3 border-b border-dashed border-slate-200 last:border-b-0 last:pb-0 break-inside-avoid">
                  <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 mb-1">
                    <h3 className="text-sm font-bold text-slate-950">
                      {edu.degree}
                    </h3>
                    <span className="text-xs font-semibold text-amber-800 shrink-0">
                      {edu.period}
                    </span>
                  </div>
                  <div className="text-xs font-semibold text-slate-500 mb-1.5">
                    {edu.institution}
                  </div>
                  {edu.description && (
                    <p className="text-xs text-slate-700 leading-relaxed text-justify mb-2">
                      {edu.description}
                    </p>
                  )}
                  {edu.skills && edu.skills.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {edu.skills.map((sk, sIdx) => (
                        <span key={sIdx} className="text-[10px] bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded">
                          {sk}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        </article>
      </div>
    </div>
  );
}

export default CV;
